import React, { useState, useMemo } from 'react';
import { Warning, Lightning, Drop } from '@phosphor-icons/react';

function getOeeColor(oee) {
  if (oee >= 85) return { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' };
  if (oee >= 78) return { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' };
  return { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' };
}

function getTariffColor(tariff) {
  if (tariff === 'Off-Peak') return '#4ade80';
  if (tariff === 'Standard') return '#facc15';
  return '#ef4444';
}

export default function OeeHeatmap({ data }) {
  const heatmap = data?.heatmap || [];
  const lines = data?.lines || [];
  const shifts = data?.shifts || [];
  const [filterLine, setFilterLine] = useState('all');
  const [filterShift, setFilterShift] = useState('all');

  const filtered = useMemo(() => {
    let d = heatmap;
    if (filterLine !== 'all') d = d.filter(h => h.line_id === filterLine);
    if (filterShift !== 'all') d = d.filter(h => h.shift === filterShift);
    return d;
  }, [heatmap, filterLine, filterShift]);

  // Group by line
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(h => {
      if (!map[h.line_id]) map[h.line_id] = [];
      map[h.line_id].push(h);
    });
    return map;
  }, [filtered]);

  const alertCount = filtered.filter(h => h.alerts?.length > 0).length;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="oee-heatmap">
      {/* Filters */}
      <div className="bg-surface border border-border rounded-lg p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-xs font-body font-semibold text-text-secondary uppercase tracking-wider">Line</label>
          <select
            data-testid="oee-heatmap-filter-line"
            value={filterLine}
            onChange={e => setFilterLine(e.target.value)}
            className="bg-background border border-border rounded-md px-3 py-1.5 text-sm font-body text-text-primary focus:ring-1 focus:ring-primary"
          >
            <option value="all">All Lines</option>
            {lines.map(l => <option key={l.line_id} value={l.line_id}>{l.name}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-body font-semibold text-text-secondary uppercase tracking-wider">Shift</label>
          <select
            data-testid="oee-heatmap-filter-shift"
            value={filterShift}
            onChange={e => setFilterShift(e.target.value)}
            className="bg-background border border-border rounded-md px-3 py-1.5 text-sm font-body text-text-primary focus:ring-1 focus:ring-primary"
          >
            <option value="all">All Shifts</option>
            {shifts.map(s => <option key={s.shift_id} value={s.name}>{s.name} ({s.start}–{s.end})</option>)}
          </select>
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs font-body text-text-secondary">
          <Warning size={14} className="text-amber-400" />
          <span>{alertCount} cells with alerts</span>
        </div>
      </div>

      {/* Heatmap Grid */}
      {Object.entries(grouped).map(([lineId, cells]) => {
        const lineInfo = lines.find(l => l.line_id === lineId);
        return (
          <div key={lineId} className="bg-surface border border-border rounded-lg p-5" data-testid={`oee-heatmap-line-${lineId}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading text-sm font-semibold text-text-primary">{lineInfo?.name || lineId}</h3>
                <p className="text-xs font-body text-text-secondary">{lineInfo?.asset_type} — Criticality: {lineInfo?.criticality}</p>
              </div>
              <span className="text-xs font-body text-text-secondary">{lineInfo?.capacity_units_hr} units/hr capacity</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
              {cells.map((cell, idx) => {
                const oeeStyle = getOeeColor(cell.oee);
                return (
                  <div key={idx} className={`border rounded-lg p-3 ${oeeStyle.border} ${oeeStyle.bg} transition-all hover:scale-[1.02]`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-body font-semibold text-text-primary">{cell.shift}</span>
                      <span className="text-[10px] font-body px-1.5 py-0.5 rounded-full" style={{ backgroundColor: getTariffColor(cell.tariff) + '30', color: getTariffColor(cell.tariff) }}>
                        {cell.tariff}
                      </span>
                    </div>
                    <p className={`font-heading text-2xl font-bold ${oeeStyle.text}`}>{cell.oee}%</p>
                    <div className="mt-2 space-y-1 text-[10px] font-body text-text-secondary">
                      <div className="flex justify-between">
                        <span className="flex items-center gap-1"><Lightning size={10} /> Energy</span>
                        <span>{cell.energy_kwh} kWh</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="flex items-center gap-1"><Drop size={10} /> Scrap</span>
                        <span>{cell.scrap_pct}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Idle kWh</span>
                        <span>{cell.idle_kwh}</span>
                      </div>
                    </div>
                    {cell.alerts?.length > 0 && (
                      <div className="mt-2 space-y-0.5">
                        {cell.alerts.map((a, i) => (
                          <div key={i} className="flex items-center gap-1 text-[9px] font-body text-amber-400">
                            <Warning size={9} weight="fill" /> {a}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Tariff Legend */}
      <div className="bg-surface border border-border rounded-lg p-4">
        <h3 className="font-heading text-sm font-semibold text-text-primary mb-3">TOU Tariff Structure (Vietnam EVN 22-110kV)</h3>
        <div className="flex flex-wrap gap-4">
          {(data?.tariffs || []).map(t => (
            <div key={t.period} className="flex items-center gap-2 text-xs font-body">
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: t.color }} />
              <span className="text-text-primary font-medium">{t.period}</span>
              <span className="text-text-secondary">{t.hours}</span>
              <span className="text-text-secondary">({t.rate_vnd_kwh?.toLocaleString()} VND/kWh)</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
